import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const CURSOR_GAP = 14;
const EDGE_GAP = 8;

/* Chart tooltips follow the cursor with viewport coordinates, so they must be
   portalled to <body>. Rendered in place they inherit the nearest ancestor that
   has a transform as their containing block — the section entrance animations
   leave an identity matrix on `.trendsRow` / the muscle panel, which is enough to
   offset a position:fixed tooltip by that row's own position.

   Size is measured after render instead of guessed so the flip near a viewport
   edge lands on the real box. */
export default function ChartTooltip({ className, x, y, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x + CURSOR_GAP, top: y + CURSOR_GAP });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();

    let left = x + CURSOR_GAP;
    if (left + width > window.innerWidth - EDGE_GAP) left = x - CURSOR_GAP - width;
    left = Math.max(EDGE_GAP, Math.min(left, window.innerWidth - width - EDGE_GAP));

    let top = y - height - EDGE_GAP;
    if (top < EDGE_GAP) top = y + CURSOR_GAP;
    top = Math.max(EDGE_GAP, Math.min(top, window.innerHeight - height - EDGE_GAP));

    // Bail out when unchanged — `children` is a fresh element every render, so
    // an unconditional setPos would re-trigger this effect forever.
    setPos((prev) => (prev.left === left && prev.top === top ? prev : { left, top }));
  }, [x, y, children]);

  return createPortal(
    <div ref={ref} className={className} style={{ left: pos.left, top: pos.top }}>
      {children}
    </div>,
    document.body
  );
}
